import { useEffect, useState } from "react";

const SUPPORT_WHATSAPP_NUMBER = "919080432374";
const SUPPORT_MESSAGE = "Hi, I am Looking for Cyber Security Training";

function PrivacyPolicy() {
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    document.title = "Privacy Policy | Practical Infosec";

    function handleScroll() {
      setShowBackToTop(window.scrollY > 300);
    }

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  function openWhatsAppChat() {
    const encoded = encodeURIComponent(SUPPORT_MESSAGE);
    const waMe = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encoded}`;

    window.open(waMe, "_blank", "noopener,noreferrer");
  }

  function openWhatsAppAppFallback() {
    const encoded = encodeURIComponent(SUPPORT_MESSAGE);
    const deepLink = `whatsapp://send?phone=${SUPPORT_WHATSAPP_NUMBER}&text=${encoded}`;
    const webLink = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encoded}`;

    window.location.href = deepLink;

    setTimeout(() => {
      window.open(webLink, "_blank", "noopener,noreferrer");
    }, 700);
  }

  return (
    <main className="relative overflow-hidden bg-slate-50">
      {/* HERO */}
      <section className="relative px-4 py-20 overflow-hidden bg-slate-950 md:px-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.35),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(14,165,233,0.25),transparent_35%),radial-gradient(circle_at_center,rgba(124,58,237,0.18),transparent_42%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.045)_1px,transparent_1px)] bg-[size:44px_44px] opacity-30"></div>
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/70 to-transparent"></div>

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest uppercase border rounded-full text-cyan-300 bg-white/10 border-white/15 backdrop-blur">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            Legal & Data Protection
          </span>

          <h1 className="mt-6 text-4xl font-black tracking-tight text-white md:text-6xl">
            🛡️ Privacy Policy
          </h1>

          <p className="max-w-3xl mx-auto mt-5 text-lg font-semibold leading-8 text-slate-300">
            This Privacy Policy explains how Practical Infosec collects, uses,
            protects, and manages your personal information when you use our
            website and related learning services.
          </p>
        </div>
      </section>

      {/* POLICY CONTENT */}
      <section className="relative px-4 py-14 md:px-20">
        <div className="absolute top-0 right-0 rounded-full w-96 h-96 bg-blue-200/30 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 rounded-full w-96 h-96 bg-cyan-200/20 blur-3xl"></div>

        <article className="relative z-10 max-w-5xl p-5 mx-auto bg-white border shadow-2xl rounded-3xl border-slate-200 md:p-10">
          {/* Policy Meta */}
          <header className="p-5 mb-10 border rounded-2xl bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 border-slate-800">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="p-4 border rounded-xl bg-white/10 border-white/10">
                <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
                  Effective Date
                </p>
                <p className="mt-1 text-base font-black text-white">
                  July 6, 2025
                </p>
              </div>

              <div className="p-4 border rounded-xl bg-white/10 border-white/10">
                <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
                  Website
                </p>
                <a
                  href="https://www.practicalinfosec.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-1 text-base font-black text-white underline"
                >
                  Practical Infosec
                </a>
              </div>

              <div className="p-4 border rounded-xl bg-white/10 border-white/10">
                <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
                  Organization
                </p>
                <p className="mt-1 text-base font-black text-white">
                  Practical Infosec Edutech Pvt. Ltd.
                </p>
              </div>

              <div className="p-4 border rounded-xl bg-white/10 border-white/10">
                <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
                  Contact
                </p>
                <p className="mt-1 text-base font-black text-white underline">
                  support@practicalinfosec.com
                </p>
              </div>
            </div>
          </header>

          <section className="space-y-8 text-slate-800">
            {/* 1 */}
            <div>
              <h2 className="mb-3 text-3xl font-black text-slate-950">
                1. Introduction
              </h2>

              <p className="mb-3 text-base font-semibold leading-7">
                Welcome to <span className="font-black">Practical Infosec</span>,
                a cybersecurity education platform that provides online learning
                resources, practical labs, and professional training programs.
              </p>

              <p className="mb-3 text-base font-semibold leading-7">
                Your privacy is very important to us. This Privacy Policy
                explains how we collect, use, disclose, and protect your personal
                information when you use our website and related services.
              </p>

              <p className="text-base font-semibold leading-7">
                By accessing or using our website, you agree to this Privacy
                Policy.
              </p>
            </div>

            {/* 2 */}
            <div>
              <h2 className="mb-3 text-3xl font-black text-slate-950">
                2. Information We Collect
              </h2>

              <p className="mb-4 text-base font-semibold leading-7">
                We collect information to provide better learning experiences
                and ensure platform security. The types of information we
                collect include:
              </p>

              <h3 className="mb-3 text-2xl font-black text-slate-900">
                a. Personal Information
              </h3>

              <p className="mb-3 text-base font-semibold leading-7">
                When you register, enroll, or interact with our website, we may
                collect:
              </p>

              <ul className="space-y-2 list-disc list-inside">
                <li className="ml-6 text-lg font-semibold">Full Name</li>
                <li className="ml-6 text-lg font-semibold">Email Address</li>
                <li className="ml-6 text-lg font-semibold">Phone Number</li>
                <li className="ml-6 text-lg font-semibold">
                  Payment details processed securely through third-party payment
                  gateways
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Course enrollment and progress data
                </li>
              </ul>
            </div>

            {/* 3 */}
            <div>
              <h2 className="mb-3 text-3xl font-black text-slate-950">
                3. How We Use Your Information
              </h2>

              <p className="mb-3 text-base font-semibold leading-7">
                We use the information we collect to:
              </p>

              <ul className="space-y-2 list-disc list-inside">
                <li className="ml-6 text-lg font-semibold">
                  Create and manage your account
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Provide access to courses, labs, and certifications
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Process payments and generate invoices
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Send course updates, notifications, and newsletters
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Improve website performance and user experience
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Detect and prevent security issues or unauthorized access
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Comply with legal and regulatory obligations
                </li>
              </ul>
            </div>

            {/* 4 */}
            <div>
              <h2 className="mb-3 text-3xl font-black text-slate-950">
                4. Cookies and Tracking Technologies
              </h2>

              <p className="mb-3 text-base font-semibold leading-7">
                We use cookies and similar technologies to:
              </p>

              <ul className="mb-4 space-y-2 list-disc list-inside">
                <li className="ml-6 text-lg font-semibold">
                  Keep you logged in
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Remember your preferences
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Analyze traffic and performance
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Personalize your learning experience
                </li>
              </ul>

              <p className="text-base font-semibold leading-7">
                You can control cookie settings through your browser, but
                disabling cookies may affect functionality.
              </p>
            </div>

            {/* 5 */}
            <div>
              <h2 className="mb-3 text-3xl font-black text-slate-950">
                5. Data Sharing and Disclosure
              </h2>

              <p className="mb-3 text-base font-semibold leading-7">
                We <strong>do not sell or rent</strong> your personal data.
              </p>

              <p className="mb-3 text-base font-semibold leading-7">
                We may share your information only with:
              </p>

              <ul className="space-y-2 list-disc list-inside">
                <li className="ml-6 text-lg font-semibold">
                  <strong>Legal Authorities </strong>
                  if required by law or in response to valid legal requests.
                </li>
                <li className="ml-6 text-lg font-semibold">
                  <strong>Service Providers </strong>
                  such as hosting, payment gateways, and email providers who
                  process data under confidentiality agreements.
                </li>
                <li className="ml-6 text-lg font-semibold">
                  <strong>Analytics Providers </strong>
                  such as Google Analytics to improve user experience.
                </li>
              </ul>
            </div>

            {/* 6 */}
            <div>
              <h2 className="mb-3 text-3xl font-black text-slate-950">
                6. Data Retention
              </h2>

              <p className="mb-3 text-base font-semibold leading-7">
                We retain your personal information only for as long as
                necessary to:
              </p>

              <ul className="mb-4 space-y-2 list-disc list-inside">
                <li className="ml-6 text-lg font-semibold">
                  Provide our services, or
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Comply with legal obligations, resolve disputes, and enforce
                  agreements.
                </li>
              </ul>

              <p className="text-base font-semibold leading-7">
                If you delete your account, certain records such as invoices and
                payment records may be retained as required by law.
              </p>
            </div>

            {/* 7 */}
            <div>
              <h2 className="mb-3 text-3xl font-black text-slate-950">
                7. Data Security
              </h2>

              <p className="mb-3 text-base font-semibold leading-7">
                We take cybersecurity seriously.
              </p>

              <p className="mb-3 text-base font-semibold leading-7">
                Practical Infosec implements:
              </p>

              <ul className="mb-4 space-y-2 list-disc list-inside">
                <li className="ml-6 text-lg font-semibold">
                  SSL/TLS encryption for data transmission
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Secure password storage using hashing
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Regular security audits and vulnerability assessments
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Role-based access control and firewalls
                </li>
              </ul>

              <p className="text-base font-semibold leading-7">
                However, no system is 100% secure. By using our website, you
                acknowledge and accept that online transmission carries inherent
                risks.
              </p>
            </div>

            {/* 8 */}
            <div>
              <h2 className="mb-3 text-3xl font-black text-slate-950">
                8. Your Rights
              </h2>

              <p className="mb-3 text-base font-semibold leading-7">
                Depending on your jurisdiction, you have the right to:
              </p>

              <ul className="mb-4 space-y-2 list-disc list-inside">
                <li className="ml-6 text-lg font-semibold">
                  Access your data
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Correct inaccurate data
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Delete your data “Right to be Forgotten”
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Withdraw consent
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Object to data processing
                </li>
                <li className="ml-6 text-lg font-semibold">
                  Request data portability
                </li>
              </ul>

              <p className="text-base font-semibold leading-7">
                You can exercise these rights by contacting{" "}
                <span className="font-black text-blue-500 underline">
                  privacy@practicalinfosec.com.
                </span>
              </p>
            </div>

            {/* 9 */}
            <div>
              <h2 className="mb-3 text-3xl font-black text-slate-950">
                9. Third-Party Links
              </h2>

              <p className="mb-3 text-base font-semibold leading-7">
                Our website may contain links to third-party platforms such as
                YouTube, LinkedIn, or partner learning tools.
              </p>

              <p className="text-base font-semibold leading-7">
                We are not responsible for the privacy practices or content of
                those sites. Please review their respective privacy policies.
              </p>
            </div>

            {/* 10 */}
            <div>
              <h2 className="mb-3 text-3xl font-black text-slate-950">
                10. Children’s Privacy
              </h2>

              <p className="mb-3 text-base font-semibold leading-7">
                Our services are not directed to children under 13 years of age.
              </p>

              <p className="text-base font-semibold leading-7">
                We do not knowingly collect personal data from minors. If you
                believe we have collected such information, please contact us to
                delete it.
              </p>
            </div>

            {/* 11 */}
            <div>
              <h2 className="mb-3 text-3xl font-black text-slate-950">
                11. Changes to This Policy
              </h2>

              <p className="mb-3 text-base font-semibold leading-7">
                We may update this Privacy Policy periodically to reflect
                changes in our practices or legal requirements.
              </p>

              <p className="text-base font-semibold leading-7">
                Updated versions will be posted on this page with a new
                “Effective Date.”
              </p>
            </div>

            {/* 12 */}
            <div className="p-5 border border-blue-100 rounded-2xl bg-blue-50">
              <h2 className="mb-3 text-3xl font-black text-slate-950">
                12. Contact Us
              </h2>

              <p className="mb-4 text-base font-semibold leading-7">
                For questions, concerns, or privacy-related requests:
              </p>

              <p className="mb-2 text-base font-semibold text-slate-800">
                <strong>📧 Email : </strong>
                <a
                  href="mailto:support@practicalinfosec.com"
                  className="italic font-black text-blue-500 underline"
                >
                  support@practicalinfosec.com
                </a>
              </p>

              <p className="mb-2 text-base font-semibold text-slate-800">
                <strong>🌐 Website : </strong>
                <a
                  href="https://www.practicalinfosec.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="italic font-black text-blue-500 underline"
                >
                  www.practicalinfosec.com
                </a>
              </p>

              <p className="text-base font-semibold text-slate-800">
                <strong>🏢 Registered Office : </strong>
                <span className="italic font-black text-blue-500 underline">
                  Practical Infosec Edutech Pvt. Ltd., Hyderabad, India
                </span>
              </p>
            </div>
          </section>
        </article>
      </section>

      {/* WHATSAPP STICKY BUTTON */}
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setWhatsappOpen((prev) => !prev);
        }}
        className="fixed z-50 p-3 px-4 text-white transition -translate-y-1/2 rounded-full shadow-lg cursor-pointer top-1/2 right-6 animate-blink hover:opacity-90 bg-gradient-to-r from-green-400 via-green-500 to-green-600"
        aria-label="Join WhatsApp"
      >
        <i className="text-4xl fa-brands fa-whatsapp"></i>
      </button>

      {/* WHATSAPP MODAL */}
      {whatsappOpen && (
        <div
          className="fixed z-50 p-4 transition-opacity duration-300 -translate-y-1/2 bg-white shadow-xl top-1/2 right-20 rounded-xl w-72 max-sm:right-4 max-sm:left-4 max-sm:w-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="whatsappTitle"
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setWhatsappOpen(false);
            }}
            className="absolute text-3xl text-gray-600 cursor-pointer top-2 left-4 hover:text-red-500"
            aria-label="Close"
          >
            &times;
          </button>

          <h2
            id="whatsappTitle"
            className="mt-6 mb-2 text-lg font-bold text-center text-gray-800"
          >
            Unsure about which course to choose?
          </h2>

          <p className="mb-3 text-sm text-center text-gray-700">
            Tap below to start a WhatsApp chat — we're here to help!
          </p>

          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={openWhatsAppChat}
              className="inline-flex items-center justify-center w-full gap-2 px-3 py-2 text-sm text-white transition bg-green-600 rounded-full hover:bg-green-700"
              aria-label="Chat Now on WhatsApp"
            >
              <i className="fa-brands fa-whatsapp"></i>
              Chat Now
            </button>

            <button
              type="button"
              onClick={openWhatsAppAppFallback}
              className="inline-flex items-center justify-center w-full gap-2 px-3 py-2 text-sm text-gray-800 transition border border-gray-200 rounded-full hover:bg-gray-50"
              aria-label="Open in WhatsApp App"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-green-600"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M20.52 3.48A11.93 11.93 0 0012 .5C5.73.5.99 5.24.99 11.5c0 2.01.53 3.95 1.53 5.68L0 24l6.97-1.79c1.61.45 3.28.68 4.9.68 6.27 0 11.01-4.74 11.01-11 0-3.02-1.18-5.85-3.36-7.41zM12 21.5c-1.44 0-2.86-.34-4.14-.99l-.3-.15-4.14 1.06 1.15-3.98-.18-.31A8.12 8.12 0 013 11.5c0-4.97 4.06-9 9-9 2.41 0 4.66.94 6.36 2.64a8.95 8.95 0 012.64 6.36c0 4.97-4.06 9-9 9z" />
              </svg>
              Open in App
            </button>
          </div>
        </div>
      )}

      {/* BACK TO TOP BUTTON */}
      {showBackToTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed z-50 p-4 text-white transition bg-blue-700 rounded-lg shadow-lg cursor-pointer bottom-12 right-7 hover:bg-blue-800"
          aria-label="Back to top"
        >
          <i className="text-xl fa-solid fa-arrow-up"></i>
        </button>
      )}

      {/* FLOATING CALL BUTTON */}
      <a
        href="tel:+91-9080432374"
        className="fixed z-50 p-3 text-white transition rounded-md shadow-lg cursor-pointer bg-violet-600 bottom-60 right-7 hover:bg-green-700"
        aria-label="Call Us"
      >
        <i className="text-2xl fa-solid fa-phone"></i>
      </a>
    </main>
  );
}

export default PrivacyPolicy;